import i18n from "../../../i18n";
import { fetchGroupLanguageCode } from "../../groups/groupAPI";
import { PronunciationPosition } from "../../settings/model/PronunciationPosition";
import {
  getLearningIntervalUnitCountFromLocalStorage,
  getMemoPositionFromLocalStorage,
  getLearningPronunciationPositionFromLocalStroage,
  getLearningReviewGroupSortStyleFromLocalStorage,
  getMultipleChoiceTimeLimitFromLocalStroage,
  isOnShowingEnglishPronunciationSelectViewFromLocalStroage,
} from "../../settings/util/learningSettings";
import Term from "../../term/Term";
import { LearningAnswerType } from "../model/LearningAnswerType";
import LearningContinue from "../model/LearningContinue";
import LearningHistoryTermsResult from "../model/LearningHistoryTerms";
import { LearningMode } from "../model/LearningMode";
import { LearningType } from "../model/LearningType";
import { LearningHistoryRange } from "../model/learningHistoryRange";
import { MemoPosition } from "../../settings/model/MemoPosition";
import { LearningReviewSortStyle } from "../model/LearningReviewSortStyle";
import { LearningTimeLimit } from "../model/LearningTimeLimit";
import { LearningAnswerSpeech } from "../model/LearningAnswerSpeech";

export const NO_LIMIT = 0;

export function getTermsUsingDefaultTerms(
  defaultTerms: Term[],
  terms: Term[]
): Term[] {
  return terms.filter((term) =>
    defaultTerms.some((defaultTerm) => defaultTerm.id === term.id)
  );
}

export function getAnswerSpeechDisplayText(item: LearningAnswerSpeech): string {
  switch (item) {
    case LearningAnswerSpeech.always:
      return i18n.t("LearningAnswerSpeechAll");
    case LearningAnswerSpeech.onlyIncorrect:
      return i18n.t("LearningAnswerSpeechIncorrect");
    case LearningAnswerSpeech.none:
      return i18n.t("LearningAnswerSpeechNone");
  }
}
export function getMultipleChoiceTimeLimitValue(): number {
  const limit = getMultipleChoiceTimeLimitFromLocalStroage();
  switch (limit) {
    case LearningTimeLimit.none:
      return 0;
    case LearningTimeLimit.slower:
      return 10;
    case LearningTimeLimit.slow:
      return 5;
    case LearningTimeLimit.normal:
      return 3;
    case LearningTimeLimit.fast:
      return 2;
  }
}

export function getMultipleChoiceTimeLimitDisplayText(): string {
  const limit = getMultipleChoiceTimeLimitFromLocalStroage();
  let seconds = 0;
  switch (limit) {
    case LearningTimeLimit.none:
      return i18n.t("MultipleChoiceTimeLimitNoTimeLimit");
    case LearningTimeLimit.slower:
      seconds = 10;
      return i18n.t("MultipleChoiceTimeLimitValue", { seconds });
    case LearningTimeLimit.slow:
      seconds = 5;
      return i18n.t("MultipleChoiceTimeLimitValue", { seconds });
    case LearningTimeLimit.normal:
      seconds = 3;
      return i18n.t("MultipleChoiceTimeLimitValue", { seconds });
    case LearningTimeLimit.fast:
      seconds = 2;
      return i18n.t("MultipleChoiceTimeLimitValue", { seconds });
  }
}

export function getMultipleChoiceTimeLimitDisplayTextWithLimit(
  limit: LearningTimeLimit
): string {
  let seconds = 0;
  switch (limit) {
    case LearningTimeLimit.none:
      return i18n.t("MultipleChoiceTimeLimitNoTimeLimit");
    case LearningTimeLimit.slower:
      seconds = 10;
      return i18n.t("MultipleChoiceTimeLimitValue", { seconds });
    case LearningTimeLimit.slow:
      seconds = 5;
      return i18n.t("MultipleChoiceTimeLimitValue", { seconds });
    case LearningTimeLimit.normal:
      seconds = 3;
      return i18n.t("MultipleChoiceTimeLimitValue", { seconds });
    case LearningTimeLimit.fast:
      seconds = 2;
      return i18n.t("MultipleChoiceTimeLimitValue", { seconds });
  }
}

export async function isShowingPronunciationSelectView(
  term: Term
): Promise<boolean> {
  const value = isOnShowingEnglishPronunciationSelectViewFromLocalStroage();
  if (!value) return false;

  if (term.group.length === 0) return false;

  try {
    const languageCode = await fetchGroupLanguageCode(term.group);
    return languageCode === "en";
  } catch (error) {
    return Promise.reject(error);
  }
}

export function getLearningAnswerTypeText(answerType: LearningAnswerType[]) {
  const correctText = i18n.t("CorrectAnswer");
  const incorrectText = i18n.t("IncorrectAnswer");

  if (answerType.length === 0) return;
  if (answerType.length === 1) {
    const item = answerType[0];
    if (item === LearningAnswerType.correct) {
      return correctText;
    } else if (item === LearningAnswerType.incorrect) {
      return incorrectText;
    } else {
      return "";
    }
  } else {
    return correctText + ", " + incorrectText;
  }
}

export function getLearningTypeText(type: LearningType) {
  switch (type) {
    case LearningType.flashcards:
      return i18n.t("Flashcard");
    case LearningType.multiplechoice:
      return i18n.t("MultipleChoiceTitle");
    case LearningType.spellingQuiz:
      return i18n.t("SpellingQuiz");
  }
}

export function getLearningRangeText(range: LearningHistoryRange) {
  switch (range) {
    case LearningHistoryRange.day:
      return i18n.t("LearningTodayLearnedText");
    case LearningHistoryRange.yesterday:
      return i18n.t("LearningYesterdayLearnedText");
    case LearningHistoryRange.three_days:
      return i18n.t("LearningThreeDaysLeanredText");
    case LearningHistoryRange.week:
      return i18n.t("LearningWeekLearnedText");
    case LearningHistoryRange.month:
      return i18n.t("LearningMonthLearnedText");
    case LearningHistoryRange.all:
      return i18n.t("LearningAllLearnedText");
  }
}

export function getDailyTargetMessage(
  learningHistoryTerms: LearningHistoryTermsResult | undefined
): string {
  if (learningHistoryTerms == null) {
    return "";
  }
  if (learningHistoryTerms.correctTermsCount < learningHistoryTerms.goalCount) {
    return i18n.t("LearningGoalMessageDayInLearning");
  } else {
    return i18n.t("LearningGoalMessageDayComplete");
  }
}

export function getPageSize(
  termsCount: number,
  learningContinue: LearningContinue | null
): number {
  if (learningContinue !== null) {
    return learningContinue.itemSize;
  }

  const intervalUnitCount = getLearningIntervalUnitCountFromLocalStorage();
  if (Number(intervalUnitCount) === NO_LIMIT) {
    return termsCount;
  } else {
    return intervalUnitCount;
  }
}

export function isLastCard(
  learnedTermsCount: number,
  totalTermsCount: number
): boolean {
  return learnedTermsCount >= totalTermsCount;
}

export function getCurrentCount(
  learningMode: LearningMode,
  currentPage: number,
  currentIndex: number,
  totalTermsCount: number,
  learningContinue: LearningContinue | null
): number {
  switch (learningMode) {
    case LearningMode.normal:
    case LearningMode.restartFromLearningHistory:
      return (
        currentPage * getPageSize(totalTermsCount, learningContinue) +
        currentIndex +
        1
      );
    case LearningMode.restartOnlyIncorrectTerms:
      return currentIndex + 1;
  }
}

export function getLearningReviewSortStyleTitle(): string {
  switch (getLearningReviewGroupSortStyleFromLocalStorage()) {
    case LearningReviewSortStyle.named:
      return i18n.t("SortByNamed");
    case LearningReviewSortStyle.termsCount:
      return i18n.t("SortByTermsCount");
    case LearningReviewSortStyle.termsCountReverse:
      return i18n.t("SortByTermsCountReverse");
    case LearningReviewSortStyle.incorrectTermsCount:
      return i18n.t("SortByIncorrectTermsCount");
  }
  return "";
}

export function getTotalCount(
  learningMode: LearningMode,
  currentPage: number,
  learningTermsCount: number,
  learningContinue: LearningContinue | null
): number {
  if (learningContinue !== null) {
    return currentPage * learningContinue.itemSize + learningTermsCount;
  }

  switch (learningMode) {
    case LearningMode.normal:
    case LearningMode.restartFromLearningHistory:
      const intervalUnitCount = getLearningIntervalUnitCountFromLocalStorage();
      if (Number(intervalUnitCount) === NO_LIMIT) {
        return learningTermsCount;
      } else {
        const totalCount = currentPage * intervalUnitCount + learningTermsCount;
        return totalCount;
      }
    case LearningMode.restartOnlyIncorrectTerms:
      return learningTermsCount;
  }
}

export function isShowMemo(
  value: string,
  front: "front" | "back",
  learningType: LearningType
): boolean {
  const memoPositionValue = getMemoPositionFromLocalStorage(learningType);
  switch (memoPositionValue) {
    case MemoPosition.onTerm:
      return value.length !== 0 && front === "front";
    case MemoPosition.onDefinition:
      return value.length !== 0 && front !== "front";
    case MemoPosition.none:
      return false;
  }
}

export function isShowPronunciation(
  value: string,
  front: "front" | "back",
  firstQuestion: "term" | "definition" | "pron"
): boolean {
  if (value.length === 0) {
    return false;
  }

  switch (firstQuestion) {
    case "term":
      if (
        getLearningPronunciationPositionFromLocalStroage() ===
        PronunciationPosition.onTerm
      ) {
        if (front === "front") {
          return true;
        }
      } else {
        if (front === "back") {
          return true;
        }
      }
      break;
    case "definition":
      if (
        getLearningPronunciationPositionFromLocalStroage() ===
        PronunciationPosition.onTerm
      ) {
        if (front === "back") {
          return true;
        }
      } else {
        if (front === "front") {
          return true;
        }
      }
      break;
    case "pron":
      if (front === "back") {
        return true;
      }
      break;
  }
  return false;
}
